import { useCallback, useMemo } from "react";
import { useAccount, useReadContract } from "wagmi";
import type { Address } from "viem";
import {
  erc20Abi,
  holderLpVaultAbi,
  lpTimeVaultAbi,
  buybackBurnVaultAbi,
  splitVaultAbi,
} from "../contracts/abis";
import {
  contractAddresses,
  missingContracts,
  isBoundAddress,
  resolveTokenAddress,
  resolveLpAddress,
} from "../contracts/addresses";

const BSC_CHAIN_ID = 56;

const DEFAULT_HOLDER_USERS: readonly bigint[] = [0n, 0n, 0n, 0n];
const DEFAULT_LP_TIME_USERS: readonly bigint[] = [0n, 0n, 0n, 0n, 0n, 0n, 0n];

const DEFAULT_SPLIT_HOLDER_BPS = 5000;
const DEFAULT_SPLIT_LP_TIME_BPS = 4000;
const DEFAULT_SPLIT_BURN_BPS = 1000;

type SplitConfigTuple = readonly [Address, Address, Address, bigint, bigint, bigint];

export interface DashboardStage {
  tokenBound: boolean;
  lpBound: boolean;
  lpTimeReady: boolean;
  buybackActive: boolean;
  lpTimePaused: boolean;
}

export interface DashboardTokenSection {
  symbol: string;
  decimals: number;
  walletBalance: bigint;
  vaultBalance: bigint;
}

export interface DashboardLpSection {
  symbol: string;
  walletBalance: bigint;
}

export interface DashboardHolderLpSection {
  registered: boolean;
  registeredBalance: bigint;
  claimable: bigint;
  minHolding: bigint;
  canRegister: boolean;
  canClaim: boolean;
  tokensSoldWarning: boolean;
  stats: readonly bigint[] | undefined;
}

export interface DashboardLpTimeSection {
  registered: boolean;
  registeredBalance: bigint;
  claimableBnb: bigint;
  holdingSeconds: bigint;
  walletLp: bigint;
  minLpHolding: bigint;
  needsRegister: boolean;
  needsSync: boolean;
  decreased: boolean;
  canClaim: boolean;
  stats: readonly bigint[] | undefined;
}

export interface DashboardBuybackSection {
  pendingBnb: bigint;
  active: boolean;
  pancakeOnly: boolean;
  minBnb: bigint;
  minInterval: bigint;
  lastExecutedAt: bigint;
  nextExecutableAt: bigint;
  pendingProgressBps: number;
  totalBnbUsed: bigint;
  totalTokensBurned: bigint;
}

export interface DashboardSplitSection {
  holderBps: number;
  lpTimeBps: number;
  burnBps: number;
}

export interface FengniaoDashboard {
  isConnected: boolean;
  account: Address | undefined;
  chainId: number | undefined;
  wrongNetwork: boolean;
  missingContracts: string[];
  isLoading: boolean;
  refetch: () => Promise<unknown>;
  stage: DashboardStage;
  token: DashboardTokenSection;
  lp: DashboardLpSection;
  holderLp: DashboardHolderLpSection;
  lpTime: DashboardLpTimeSection;
  buyback: DashboardBuybackSection;
  split: DashboardSplitSection;
}

export function useFengniaoDashboard(): FengniaoDashboard {
  const { address: account, isConnected, chainId } = useAccount();

  // -----------------------------------------------------------------------
  // 1. Address discovery reads (token/LP bindings)
  // -----------------------------------------------------------------------

  const holderTaxToken = useReadContract({
    address: contractAddresses.holderLpVault,
    abi: holderLpVaultAbi,
    functionName: "taxToken",
    query: { enabled: Boolean(contractAddresses.holderLpVault) },
  });

  const holderLpToken = useReadContract({
    address: contractAddresses.holderLpVault,
    abi: holderLpVaultAbi,
    functionName: "lpToken",
    query: { enabled: Boolean(contractAddresses.holderLpVault) },
  });

  const lpTimeTokenAddr = useReadContract({
    address: contractAddresses.lpTimeVault,
    abi: lpTimeVaultAbi,
    functionName: "token",
    query: { enabled: Boolean(contractAddresses.lpTimeVault) },
  });

  const tokenAddress = resolveTokenAddress(holderTaxToken.data as Address | undefined);
  const lpAddress = resolveLpAddress(
    holderLpToken.data as Address | undefined,
    lpTimeTokenAddr.data as Address | undefined,
  );

  // -----------------------------------------------------------------------
  // 2. Holder LP vault reads
  // -----------------------------------------------------------------------

  const holderUserInfo = useReadContract({
    address: contractAddresses.holderLpVault,
    abi: holderLpVaultAbi,
    functionName: "users",
    args: account ? [account] : undefined,
    query: { enabled: Boolean(contractAddresses.holderLpVault && account) },
  });

  const holderEarned = useReadContract({
    address: contractAddresses.holderLpVault,
    abi: holderLpVaultAbi,
    functionName: "earned",
    args: account ? [account] : undefined,
    query: { enabled: Boolean(contractAddresses.holderLpVault && account) },
  });

  const holderStats = useReadContract({
    address: contractAddresses.holderLpVault,
    abi: holderLpVaultAbi,
    functionName: "getStats",
    query: { enabled: Boolean(contractAddresses.holderLpVault) },
  });

  const minHolding = useReadContract({
    address: contractAddresses.holderLpVault,
    abi: holderLpVaultAbi,
    functionName: "minHolding",
    query: { enabled: Boolean(contractAddresses.holderLpVault) },
  });

  // -----------------------------------------------------------------------
  // 3. LP-time vault reads
  // -----------------------------------------------------------------------

  const lpTimeUserInfo = useReadContract({
    address: contractAddresses.lpTimeVault,
    abi: lpTimeVaultAbi,
    functionName: "users",
    args: account ? [account] : undefined,
    query: { enabled: Boolean(contractAddresses.lpTimeVault && account) },
  });

  const lpTimeEarned = useReadContract({
    address: contractAddresses.lpTimeVault,
    abi: lpTimeVaultAbi,
    functionName: "earned",
    args: account ? [account] : undefined,
    query: { enabled: Boolean(contractAddresses.lpTimeVault && account) },
  });

  const lpTimeStats = useReadContract({
    address: contractAddresses.lpTimeVault,
    abi: lpTimeVaultAbi,
    functionName: "getStats",
    query: { enabled: Boolean(contractAddresses.lpTimeVault) },
  });

  const lpTimePaused = useReadContract({
    address: contractAddresses.lpTimeVault,
    abi: lpTimeVaultAbi,
    functionName: "paused",
    query: { enabled: Boolean(contractAddresses.lpTimeVault) },
  });

  const minLpHolding = useReadContract({
    address: contractAddresses.lpTimeVault,
    abi: lpTimeVaultAbi,
    functionName: "minLpHolding",
    query: { enabled: Boolean(contractAddresses.lpTimeVault) },
  });

  // -----------------------------------------------------------------------
  // 4. Buyback / burn vault reads
  // -----------------------------------------------------------------------

  const buybackPending = useReadContract({
    address: contractAddresses.buybackBurnVault,
    abi: buybackBurnVaultAbi,
    functionName: "pendingBNB",
    query: { enabled: Boolean(contractAddresses.buybackBurnVault) },
  });

  const buybackActive = useReadContract({
    address: contractAddresses.buybackBurnVault,
    abi: buybackBurnVaultAbi,
    functionName: "active",
    query: { enabled: Boolean(contractAddresses.buybackBurnVault) },
  });

  const buybackPancakeOnly = useReadContract({
    address: contractAddresses.buybackBurnVault,
    abi: buybackBurnVaultAbi,
    functionName: "pancakeOnly",
    query: { enabled: Boolean(contractAddresses.buybackBurnVault) },
  });

  const buybackMinBnb = useReadContract({
    address: contractAddresses.buybackBurnVault,
    abi: buybackBurnVaultAbi,
    functionName: "minBNBForExecution",
    query: { enabled: Boolean(contractAddresses.buybackBurnVault) },
  });

  const buybackMinInterval = useReadContract({
    address: contractAddresses.buybackBurnVault,
    abi: buybackBurnVaultAbi,
    functionName: "minExecutionInterval",
    query: { enabled: Boolean(contractAddresses.buybackBurnVault) },
  });

  const buybackLastExecuted = useReadContract({
    address: contractAddresses.buybackBurnVault,
    abi: buybackBurnVaultAbi,
    functionName: "lastExecutedAt",
    query: { enabled: Boolean(contractAddresses.buybackBurnVault) },
  });

  const buybackTotalBnb = useReadContract({
    address: contractAddresses.buybackBurnVault,
    abi: buybackBurnVaultAbi,
    functionName: "totalBnbUsed",
    query: { enabled: Boolean(contractAddresses.buybackBurnVault) },
  });

  const buybackTotalBurned = useReadContract({
    address: contractAddresses.buybackBurnVault,
    abi: buybackBurnVaultAbi,
    functionName: "totalTokensBurned",
    query: { enabled: Boolean(contractAddresses.buybackBurnVault) },
  });

  // -----------------------------------------------------------------------
  // 5. Split vault reads
  // -----------------------------------------------------------------------

  const splitConfig = useReadContract({
    address: contractAddresses.splitVault,
    abi: splitVaultAbi,
    functionName: "getConfig",
    query: { enabled: Boolean(contractAddresses.splitVault) },
  });

  // -----------------------------------------------------------------------
  // 6. Token reads (resolved address)
  // -----------------------------------------------------------------------

  const tokenSymbol = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: "symbol",
    query: { enabled: Boolean(tokenAddress) },
  });

  const tokenDecimals = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: "decimals",
    query: { enabled: Boolean(tokenAddress) },
  });

  const walletTokenBalance = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: account ? [account] : undefined,
    query: { enabled: Boolean(tokenAddress && account) },
  });

  const vaultTokenBalance = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: contractAddresses.holderLpVault ? [contractAddresses.holderLpVault] : undefined,
    query: { enabled: Boolean(tokenAddress && contractAddresses.holderLpVault) },
  });

  // -----------------------------------------------------------------------
  // 7. LP reads (resolved address)
  // -----------------------------------------------------------------------

  const lpSymbol = useReadContract({
    address: lpAddress,
    abi: erc20Abi,
    functionName: "symbol",
    query: { enabled: Boolean(lpAddress) },
  });

  const walletLpBalance = useReadContract({
    address: lpAddress,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: account ? [account] : undefined,
    query: { enabled: Boolean(lpAddress && account) },
  });

  // -----------------------------------------------------------------------
  // 8. Aggregate refetch (every read participates)
  // -----------------------------------------------------------------------

  const refetch = useCallback(async () => {
    return Promise.all([
      holderTaxToken.refetch(),
      holderLpToken.refetch(),
      lpTimeTokenAddr.refetch(),
      holderUserInfo.refetch(),
      holderEarned.refetch(),
      holderStats.refetch(),
      minHolding.refetch(),
      lpTimeUserInfo.refetch(),
      lpTimeEarned.refetch(),
      lpTimeStats.refetch(),
      lpTimePaused.refetch(),
      minLpHolding.refetch(),
      buybackPending.refetch(),
      buybackActive.refetch(),
      buybackPancakeOnly.refetch(),
      buybackMinBnb.refetch(),
      buybackMinInterval.refetch(),
      buybackLastExecuted.refetch(),
      buybackTotalBnb.refetch(),
      buybackTotalBurned.refetch(),
      splitConfig.refetch(),
      tokenSymbol.refetch(),
      tokenDecimals.refetch(),
      walletTokenBalance.refetch(),
      vaultTokenBalance.refetch(),
      lpSymbol.refetch(),
      walletLpBalance.refetch(),
    ]);
  }, [
    holderTaxToken,
    holderLpToken,
    lpTimeTokenAddr,
    holderUserInfo,
    holderEarned,
    holderStats,
    minHolding,
    lpTimeUserInfo,
    lpTimeEarned,
    lpTimeStats,
    lpTimePaused,
    minLpHolding,
    buybackPending,
    buybackActive,
    buybackPancakeOnly,
    buybackMinBnb,
    buybackMinInterval,
    buybackLastExecuted,
    buybackTotalBnb,
    buybackTotalBurned,
    splitConfig,
    tokenSymbol,
    tokenDecimals,
    walletTokenBalance,
    vaultTokenBalance,
    lpSymbol,
    walletLpBalance,
  ]);

  const isLoading =
    holderTaxToken.isLoading ||
    holderLpToken.isLoading ||
    lpTimeTokenAddr.isLoading ||
    holderUserInfo.isLoading ||
    holderEarned.isLoading ||
    holderStats.isLoading ||
    minHolding.isLoading ||
    lpTimeUserInfo.isLoading ||
    lpTimeEarned.isLoading ||
    lpTimeStats.isLoading ||
    lpTimePaused.isLoading ||
    minLpHolding.isLoading ||
    buybackPending.isLoading ||
    buybackActive.isLoading ||
    buybackPancakeOnly.isLoading ||
    buybackMinBnb.isLoading ||
    buybackMinInterval.isLoading ||
    buybackLastExecuted.isLoading ||
    buybackTotalBnb.isLoading ||
    buybackTotalBurned.isLoading ||
    splitConfig.isLoading ||
    tokenSymbol.isLoading ||
    tokenDecimals.isLoading ||
    walletTokenBalance.isLoading ||
    vaultTokenBalance.isLoading ||
    lpSymbol.isLoading ||
    walletLpBalance.isLoading;

  // -----------------------------------------------------------------------
  // 9. Derived bag (memoized)
  // -----------------------------------------------------------------------

  return useMemo<FengniaoDashboard>(() => {
    const wrongNetwork = chainId !== undefined && chainId !== BSC_CHAIN_ID;

    const lpTimeReady =
      isBoundAddress(lpTimeTokenAddr.data as Address | undefined) ||
      Boolean(contractAddresses.lpPairOverride);

    const stage: DashboardStage = {
      tokenBound: Boolean(tokenAddress),
      lpBound: Boolean(lpAddress),
      lpTimeReady,
      buybackActive: Boolean(buybackActive.data),
      lpTimePaused: Boolean(lpTimePaused.data),
    };

    // Token section
    const tokenWallet = (walletTokenBalance.data as bigint | undefined) ?? 0n;
    const tokenVault = (vaultTokenBalance.data as bigint | undefined) ?? 0n;
    const tokenDecimalsValue =
      typeof tokenDecimals.data === "number" ? tokenDecimals.data : 18;
    const tokenSymbolValue =
      typeof tokenSymbol.data === "string" && tokenSymbol.data.length > 0
        ? tokenSymbol.data
        : "蜂鸟";

    const token: DashboardTokenSection = {
      symbol: tokenSymbolValue,
      decimals: tokenDecimalsValue,
      walletBalance: tokenWallet,
      vaultBalance: tokenVault,
    };

    // LP section
    const lpWallet = (walletLpBalance.data as bigint | undefined) ?? 0n;
    const lpSymbolValue =
      typeof lpSymbol.data === "string" && lpSymbol.data.length > 0 ? lpSymbol.data : "LP";

    const lp: DashboardLpSection = {
      symbol: lpSymbolValue,
      walletBalance: lpWallet,
    };

    // Holder LP section
    const holderUsersTuple =
      (holderUserInfo.data as readonly bigint[] | undefined) ?? DEFAULT_HOLDER_USERS;
    const holderRegisteredBalance = holderUsersTuple[0] ?? 0n;
    const holderRegistered = holderRegisteredBalance > 0n;
    const holderClaimable = (holderEarned.data as bigint | undefined) ?? 0n;
    const minHoldingValue = (minHolding.data as bigint | undefined) ?? 0n;

    const canRegisterHolder =
      !holderRegistered &&
      tokenWallet >= minHoldingValue &&
      minHoldingValue >= 0n &&
      stage.tokenBound &&
      isConnected;

    const canClaimHolder =
      holderRegistered && holderClaimable > 0n && stage.lpBound && isConnected;

    const tokensSoldWarning = holderRegistered && tokenWallet < holderRegisteredBalance;

    const holderLp: DashboardHolderLpSection = {
      registered: holderRegistered,
      registeredBalance: holderRegisteredBalance,
      claimable: holderClaimable,
      minHolding: minHoldingValue,
      canRegister: canRegisterHolder,
      canClaim: canClaimHolder,
      tokensSoldWarning,
      stats: holderStats.data as readonly bigint[] | undefined,
    };

    // LP-time section
    const lpTimeUsersTuple =
      (lpTimeUserInfo.data as readonly bigint[] | undefined) ?? DEFAULT_LP_TIME_USERS;
    const lpTimeRegisteredBalance = lpTimeUsersTuple[0] ?? 0n;
    const lpTimeStartTime = lpTimeUsersTuple[1] ?? 0n;
    const lpTimeRegistered = lpTimeRegisteredBalance > 0n;
    const lpTimeClaimable = (lpTimeEarned.data as bigint | undefined) ?? 0n;
    const minLpHoldingValue = (minLpHolding.data as bigint | undefined) ?? 0n;

    const nowSec = BigInt(Math.floor(Date.now() / 1000));
    const holdingSeconds =
      lpTimeRegistered && lpTimeStartTime > 0n && nowSec > lpTimeStartTime
        ? nowSec - lpTimeStartTime
        : 0n;

    const lpTimePausedValue = Boolean(lpTimePaused.data);
    const needsRegisterLpTime =
      !lpTimeRegistered &&
      lpWallet > 0n &&
      lpWallet >= minLpHoldingValue &&
      stage.lpTimeReady &&
      !lpTimePausedValue &&
      isConnected;

    const needsSyncLpTime =
      lpTimeRegistered && lpWallet > lpTimeRegisteredBalance && !lpTimePausedValue;

    const decreasedLpTime = lpTimeRegistered && lpWallet < lpTimeRegisteredBalance;

    const canClaimLpTime = lpTimeClaimable > 0n && !lpTimePausedValue && isConnected;

    const lpTime: DashboardLpTimeSection = {
      registered: lpTimeRegistered,
      registeredBalance: lpTimeRegisteredBalance,
      claimableBnb: lpTimeClaimable,
      holdingSeconds,
      walletLp: lpWallet,
      minLpHolding: minLpHoldingValue,
      needsRegister: needsRegisterLpTime,
      needsSync: needsSyncLpTime,
      decreased: decreasedLpTime,
      canClaim: canClaimLpTime,
      stats: lpTimeStats.data as readonly bigint[] | undefined,
    };

    // Buyback section
    const pendingBnb = (buybackPending.data as bigint | undefined) ?? 0n;
    const minBnb = (buybackMinBnb.data as bigint | undefined) ?? 0n;
    const minInterval = (buybackMinInterval.data as bigint | undefined) ?? 0n;
    const lastExecutedAt = (buybackLastExecuted.data as bigint | undefined) ?? 0n;
    const nextExecutableAt = lastExecutedAt + minInterval;

    // pendingProgressBps: clamp(pendingBnb * 10000n / minBnb, 0, 10000)
    let pendingProgressBps = 0;
    if (minBnb > 0n) {
      const rawBps = (pendingBnb * 10_000n) / minBnb;
      const clamped = rawBps > 10_000n ? 10_000n : rawBps;
      pendingProgressBps = Math.min(10_000, Number(clamped));
    }

    const buyback: DashboardBuybackSection = {
      pendingBnb,
      active: Boolean(buybackActive.data),
      pancakeOnly: Boolean(buybackPancakeOnly.data),
      minBnb,
      minInterval,
      lastExecutedAt,
      nextExecutableAt,
      pendingProgressBps,
      totalBnbUsed: (buybackTotalBnb.data as bigint | undefined) ?? 0n,
      totalTokensBurned: (buybackTotalBurned.data as bigint | undefined) ?? 0n,
    };

    // Split section
    const splitTuple = splitConfig.data as SplitConfigTuple | undefined;
    const split: DashboardSplitSection = splitTuple
      ? {
          holderBps: Number(splitTuple[3]),
          lpTimeBps: Number(splitTuple[4]),
          burnBps: Number(splitTuple[5]),
        }
      : {
          holderBps: DEFAULT_SPLIT_HOLDER_BPS,
          lpTimeBps: DEFAULT_SPLIT_LP_TIME_BPS,
          burnBps: DEFAULT_SPLIT_BURN_BPS,
        };

    return {
      isConnected,
      account,
      chainId,
      wrongNetwork,
      missingContracts: missingContracts.filter((name): name is string => typeof name === "string"),
      isLoading,
      refetch,
      stage,
      token,
      lp,
      holderLp,
      lpTime,
      buyback,
      split,
    };
    // Memo deps: every read's .data + isLoading + identity-stable refetch + account/chain
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isConnected,
    account,
    chainId,
    isLoading,
    refetch,
    tokenAddress,
    lpAddress,
    holderTaxToken.data,
    holderLpToken.data,
    lpTimeTokenAddr.data,
    holderUserInfo.data,
    holderEarned.data,
    holderStats.data,
    minHolding.data,
    lpTimeUserInfo.data,
    lpTimeEarned.data,
    lpTimeStats.data,
    lpTimePaused.data,
    minLpHolding.data,
    buybackPending.data,
    buybackActive.data,
    buybackPancakeOnly.data,
    buybackMinBnb.data,
    buybackMinInterval.data,
    buybackLastExecuted.data,
    buybackTotalBnb.data,
    buybackTotalBurned.data,
    splitConfig.data,
    tokenSymbol.data,
    tokenDecimals.data,
    walletTokenBalance.data,
    vaultTokenBalance.data,
    lpSymbol.data,
    walletLpBalance.data,
  ]);
}
